import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Database, Play, Download, RefreshCw, CheckCircle2, XCircle, Clock, RotateCcw, AlertTriangle } from "lucide-react";

/**
 * SA UI za pregled dnevnih backupov + ročni sprožilec + download.
 */
const SuperAdminBackups = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ backups: [], retention_days: 30 });
  const [triggering, setTriggering] = useState(false);
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sa/backup`, { headers });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka pri nalaganju");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const triggerNow = async () => {
    setTriggering(true);
    try {
      await axios.post(`${API}/sa/backup/run`, {}, { headers });
      toast.success("Backup sprožen. Osvežujem seznam čez 30 s ...");
      setTimeout(() => { fetchData(); setTriggering(false); }, 30000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka");
      setTriggering(false);
    }
  };

  const downloadBackup = async (id) => {
    try {
      const res = await axios.get(`${API}/sa/backup/${id}/download`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${id}.tar.gz`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka pri prenosu");
    }
  };

  // ---- Restore wizard ----
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null); // whole backup doc
  const [restoreMode, setRestoreMode] = useState("merge");
  const [restoreConfirm, setRestoreConfirm] = useState("");
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);

  const openRestore = (backup) => {
    setRestoreTarget(backup);
    setRestoreMode("merge");
    setRestoreConfirm("");
    setRestoreResult(null);
    setRestoreOpen(true);
  };

  const runRestore = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    setRestoreResult(null);
    try {
      const res = await axios.post(
        `${API}/sa/backup/${restoreTarget.id}/restore`,
        { mode: restoreMode, confirm: restoreConfirm },
        { headers },
      );
      setRestoreResult(res.data);
      toast.success(`Obnova zaključena (${restoreMode})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka pri obnovi");
    } finally {
      setRestoring(false);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const active = (data.backups || []).filter(b => !b.is_deleted);
  const expired = (data.backups || []).filter(b => b.is_deleted);

  return (
    <div className="space-y-6" data-testid="sa-backups">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-6 w-6" />
            Dnevni backup
          </h2>
          <p className="text-sm text-muted-foreground">
            Avtomatski backup MongoDB baze ob 03:00 CET, ločen po lovskih družinah, {data.retention_days}-dnevna retencija. Shramba: Emergent Object Storage.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Osveži
          </Button>
          <Button onClick={triggerNow} disabled={triggering} data-testid="sa-backup-trigger">
            <Play className="h-4 w-4 mr-2" />
            {triggering ? "Se izvaja ..." : "Sproži zdaj"}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Aktivni backupi ({active.length})</CardTitle>
          <CardDescription>Zapisi znotraj {data.retention_days} dni; še dostopni za prenos.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {active.length === 0 && !loading ? (
            <div className="text-center py-10 text-muted-foreground">Še ni backupov. Klikni "Sproži zdaj" za prvi.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left p-2">Datum</th>
                    <th className="text-left p-2">LD</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Vir</th>
                    <th className="text-left p-2">Dok.</th>
                    <th className="text-left p-2">Velikost</th>
                    <th className="text-left p-2">Trajanje</th>
                    <th className="text-right p-2">Akcije</th>
                  </tr>
                </thead>
                <tbody>
                  {active.map(b => (
                    <tr key={b.id} className="border-t hover:bg-muted/20" data-testid={`sa-backup-row-${b.id}`}>
                      <td className="p-2 font-mono text-xs">{(b.id || "").split("__")[0]}</td>
                      <td className="p-2">
                        <div className="text-sm font-medium">{b.ld_naziv || "—"}</div>
                        <div className="text-xs text-muted-foreground font-mono">{b.ld_id || ""}</div>
                      </td>
                      <td className="p-2">
                        {b.status === "success" && <Badge variant="secondary" className="bg-green-100 text-green-800"><CheckCircle2 className="h-3 w-3 mr-1 inline" />uspešen</Badge>}
                        {b.status === "error" && <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1 inline" />napaka</Badge>}
                        {b.status === "skipped" && <Badge variant="outline" className="text-muted-foreground">prazen</Badge>}
                        {b.status === "running" && <Badge variant="outline"><Clock className="h-3 w-3 mr-1 inline animate-spin" />teče</Badge>}
                        {b.error && <div className="text-xs text-red-700 mt-1">{b.error}</div>}
                        {b.note && <div className="text-xs text-muted-foreground mt-1">{b.note}</div>}
                      </td>
                      <td className="p-2">{b.trigger}</td>
                      <td className="p-2 text-xs">{b.doc_count ?? "—"}</td>
                      <td className="p-2">{formatSize(b.size)}</td>
                      <td className="p-2">{b.duration_seconds ? `${b.duration_seconds.toFixed(1)}s` : "—"}</td>
                      <td className="p-2 text-right">
                        {b.status === "success" && (
                          <div className="inline-flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => downloadBackup(b.id)} data-testid={`sa-backup-download-${b.id}`}>
                              <Download className="h-3.5 w-3.5 mr-1" />Prenesi
                            </Button>
                            {b.ld_id && (
                              <Button size="sm" variant="outline" onClick={() => openRestore(b)} data-testid={`sa-backup-restore-${b.id}`}>
                                <RotateCcw className="h-3.5 w-3.5 mr-1" />Obnovi
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {expired.length > 0 && (
        <Card className="opacity-70">
          <CardHeader>
            <CardTitle className="text-base">Potekli ({expired.length})</CardTitle>
            <CardDescription>Zapisi starejši od {data.retention_days} dni. Datoteka je še v shrambi, dostop pa je onemogočen.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-xs text-muted-foreground max-h-32 overflow-y-auto">
              {expired.map(b => (
                <div key={b.id} className="py-0.5">{b.id} · {formatSize(b.size)}</div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Restore wizard */}
      <Dialog open={restoreOpen} onOpenChange={(o) => { if (!restoring) setRestoreOpen(o); }}>
        <DialogContent className="max-w-lg" data-testid="sa-backup-restore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5" /> Obnovi backup
            </DialogTitle>
            <DialogDescription>
              Obnovi podatke LD <b>{restoreTarget?.ld_naziv || restoreTarget?.ld_id}</b> iz backupa <span className="font-mono text-xs">{restoreTarget?.id}</span>.
            </DialogDescription>
          </DialogHeader>

          {!restoreResult ? (
            <div className="space-y-4">
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex gap-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  <b>Pozor:</b> Ta operacija vpliva samo na podatke izbrane LD. Drugi LD-ji ostanejo nedotaknjeni.
                  Priporočamo najprej način <b>Spoji (merge)</b>, ki obstoječe zapise zamenja z istim ID-jem.
                </div>
              </div>

              <div>
                <Label>Način obnove</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button
                    type="button"
                    className={`text-left border rounded-md p-3 hover:bg-muted/40 ${restoreMode === "merge" ? "border-primary bg-primary/5" : ""}`}
                    onClick={() => setRestoreMode("merge")}
                    data-testid="restore-mode-merge"
                  >
                    <div className="font-medium text-sm">Spoji (merge)</div>
                    <div className="text-xs text-muted-foreground">Upsert po ID. Obstoječi zapisi se posodobijo, manjkajoči dodajo. Varnejše.</div>
                  </button>
                  <button
                    type="button"
                    className={`text-left border rounded-md p-3 hover:bg-muted/40 ${restoreMode === "replace" ? "border-destructive bg-red-50" : ""}`}
                    onClick={() => setRestoreMode("replace")}
                    data-testid="restore-mode-replace"
                  >
                    <div className="font-medium text-sm text-red-700">Zamenjaj (replace)</div>
                    <div className="text-xs text-muted-foreground">Najprej pobriše vse obstoječe zapise LD, potem vstavi iz backupa. Nevarno.</div>
                  </button>
                </div>
              </div>

              <div>
                <Label>Potrditev</Label>
                <Input
                  value={restoreConfirm}
                  onChange={(e) => setRestoreConfirm(e.target.value)}
                  placeholder={`RESTORE-${restoreTarget?.ld_id || ""}`}
                  className="font-mono mt-1"
                  data-testid="restore-confirm-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Vpiši točno: <span className="font-mono">RESTORE-{restoreTarget?.ld_id}</span>
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto text-sm">
              <div className="rounded-md bg-green-50 border border-green-200 p-3 text-green-900">
                Obnova uspešna · {restoreResult.mode} · {restoreResult.duration_seconds?.toFixed(2)}s
              </div>
              <table className="w-full text-xs">
                <thead className="text-muted-foreground">
                  <tr><th className="text-left">Zbirka</th><th>Vstavljeno</th><th>Zamenjano</th><th>Izbrisano</th></tr>
                </thead>
                <tbody>
                  {Object.entries(restoreResult.results || {}).map(([coll, s]) => (
                    <tr key={coll} className="border-t">
                      <td className="py-1 font-mono">{coll}</td>
                      <td className="text-center">{s.inserted ?? "—"}</td>
                      <td className="text-center">{s.replaced ?? "—"}</td>
                      <td className="text-center">{s.deleted ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <DialogFooter>
            {!restoreResult ? (
              <>
                <Button variant="outline" onClick={() => setRestoreOpen(false)} disabled={restoring}>Prekliči</Button>
                <Button
                  onClick={runRestore}
                  disabled={restoring || restoreConfirm !== `RESTORE-${restoreTarget?.ld_id}`}
                  variant={restoreMode === "replace" ? "destructive" : "default"}
                  data-testid="restore-execute-btn"
                >
                  {restoring ? "Obnavljam ..." : "Obnovi"}
                </Button>
              </>
            ) : (
              <Button onClick={() => setRestoreOpen(false)}>Zapri</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminBackups;
